import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Badge, Box, Button, Card, Separator, Text } from "@salestrak/ui";

import { useAuth } from "#app/lib/auth-context";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import type { WorkspaceDeletionUserRow } from "#app/lib/data";
import { useAuthEmail } from "#app/lib/hooks/use-auth-email";
import { daysLeftLabel, useWorkspaceDeletion } from "#app/lib/hooks/use-workspace-deletion";
import { formatDate } from "#app/lib/utils/format-date";
import { DateText } from "#app/components/date-text";
import { EntityTag } from "#app/components/entity";
import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";
import { LeaveWorkspaceDialog } from "#app/components/workspace-membership/leave-workspace-dialog";

// A user named by the pending-deletion state, rendered as the entity chip.
// Never interactive: the workspace is frozen, so the preview card and the
// profile it would open have no API to read from.
function DeletionUserTag({ user }: { user: WorkspaceDeletionUserRow }) {
  return (
    <EntityTag
      entity={{
        kind: "user",
        id: user.userId,
        user: {
          name: user.name,
          email: user.email,
          profileImage: user.profileImage,
          avatarColor: user.avatarColor,
          memberStatus: user.memberStatus,
        },
      }}
      interactive={false}
      size="sm"
    />
  );
}

/**
 * What stands where the shell would be while a workspace is scheduled for
 * deletion. The workspace is frozen server-side, so there is nothing to
 * render behind this. The card keeps the workspace's identity (its tile and
 * name — this is still its page), makes the date the hero fact, names who
 * scheduled it, and ends on the one thing that can happen next: an Owner
 * restores it. Members who are not Owners are shown the Owners instead, so
 * "ask an Owner" has names attached. Nothing here is irreversible.
 *
 * A deep link into the frozen workspace lands here too, so the screen makes
 * it the active one (the restore mutation's `workspaceid` header reads the
 * active workspace).
 */
export function WorkspacePendingDeletionScreen({ workspace }: { workspace: Workspace }) {
  const email = useAuthEmail();
  const { signOut } = useAuth();
  const { activeWorkspace, workspaces, switchWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { cancelDeletion, busy } = useWorkspaceDeletion(workspace.workspaceId);
  const [leaveOpen, setLeaveOpen] = useState(false);

  useEffect(() => {
    if (activeWorkspace?.workspaceId !== workspace.workspaceId) {
      void switchWorkspace(workspace);
    }
  }, [activeWorkspace?.workspaceId, workspace, switchWorkspace]);

  const deletion = workspace.deletion;
  const otherWorkspaces = workspaces.some(
    (w) => w.workspaceId !== workspace.workspaceId && w.memberStatus === "active",
  );
  const goToPicker = () => navigate({ to: "/workspace-select" });

  return (
    <WorkspaceScreenLayout
      backLabel={otherWorkspaces ? "Choose a workspace" : "Back to login"}
      backSignsOut={!otherWorkspaces}
      email={email}
      onBack={otherWorkspaces ? goToPicker : signOut}
    >
      <Box gap="md">
        <Card>
          {/* Identity: the same tile + name the picker row wears. */}
          <Box direction="row" align="center" gap="md" padding="lg">
            <WorkspaceAvatar
              avatarColor={workspace.avatarColor}
              logo={workspace.settings?.logo}
              name={workspace.name}
              size="default"
            />
            <Box gap="2xs" minWidth={0}>
              <Text size="lg" weight="semibold" truncate>
                {workspace.name}
              </Text>
              <Text size="sm" variant="muted" truncate>
                /{workspace.urlPath}
              </Text>
            </Box>
          </Box>

          <Separator />

          {/* The hero fact: when. Then who. */}
          <Box gap="sm" padding="lg">
            <Text size="xs" variant="muted" weight="medium">
              Scheduled for permanent deletion
            </Text>
            {deletion && (
              <Box direction="row" align="center" gap="sm" wrap>
                <Text size="xl" weight="semibold">
                  {formatDate(deletion.scheduledAt)}
                </Text>
                <Badge color="red" size="tag" tone="tonal">
                  in {daysLeftLabel(deletion.scheduledAt)}
                </Badge>
              </Box>
            )}
            {deletion && (
              <Box direction="row" align="center" gap="sm" wrap>
                <Text size="sm" variant="muted">
                  Scheduled by
                </Text>
                {deletion.requestedBy ? (
                  <DeletionUserTag user={deletion.requestedBy} />
                ) : (
                  <Text size="sm">a workspace Owner</Text>
                )}
                <DateText date={deletion.requestedAt} format="relative" size="sm" />
              </Box>
            )}
          </Box>

          <Separator />

          <Box gap="lg" padding="lg">
            <Text size="sm" variant="muted">
              Until then the workspace is frozen — nobody can open it, and
              nothing in it is removed before the date. Any Owner can restore
              it, and everything comes back exactly as it was.
            </Text>

            {workspace.viewerIsOwner ? (
              <Button
                disabled={busy}
                loading={busy}
                onClick={() => void cancelDeletion()}
                size="lg"
              >
                Restore workspace
              </Button>
            ) : (
              deletion &&
              (deletion.owners.length > 0 ? (
                <Box gap="sm">
                  <Text size="sm" weight="medium">
                    Only an Owner can restore it
                  </Text>
                  <Box direction="row" gap="sm" wrap>
                    {deletion.owners.map((owner) => (
                      <DeletionUserTag key={owner.userId} user={owner} />
                    ))}
                  </Box>
                </Box>
              ) : (
                // Every Owner has since left: nobody in the workspace can
                // restore it, so say so rather than show an empty roster.
                <Text size="sm" weight="medium">
                  This workspace has no Owner left to restore it. Contact SalesTrak support.
                </Text>
              ))
            )}
          </Box>
        </Card>

        {/* A member may walk away from a frozen workspace — the profile
          * settings that usually host this are behind the freeze. The Owner
          * rules still hold inside the dialog. */}
        <Box direction="row" justify="center" gap="sm" wrap>
          {otherWorkspaces && (
            <Button onClick={goToPicker} variant="ghost">
              Choose another workspace
            </Button>
          )}
          <Button onClick={() => setLeaveOpen(true)} color="destructive" variant="ghost">
            Leave workspace...
          </Button>
        </Box>
        {leaveOpen ? (
          <LeaveWorkspaceDialog
            workspace={workspace}
            onOpenChange={(open) => {
              if (!open) setLeaveOpen(false);
            }}
          />
        ) : null}
      </Box>
    </WorkspaceScreenLayout>
  );
}
